import classNames from 'classnames';
import { Field, useFormikContext } from 'formik';
import { ReactNode, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { getInitialAddressValues } from '../../../utils/address';
import { AddressSuggestion, useCheckoutState } from '../../../utils/checkoutState';
import { Country, config, getAssetUrl } from '../../../utils/config';
import { FormNameProvider } from '../../../utils/formName';
import getCarriers from '../../../utils/getCarriers';
import { useModal } from '../../../utils/modal';
import Button from '../../atoms/Button';
import Paragraph from '../../atoms/Paragraph';
import SubHeadline from '../../atoms/SubHeadline';
import AddressField from '../../fields/AddressField';
import AddressFieldBg from '../../fields/AddressFieldBg';
import AddressFieldNl from '../../fields/AddressFieldNl';
import AddressFieldUS from '../../fields/AddressFieldUS';
import GenderField from '../../fields/GenderField';
import NamesField from '../../fields/NamesField';
import CheckboxField from '../../formik/CheckboxField';
import SelectField from '../../formik/SelectField';
import AddressBook from '../AddressBook';
import { AddressWithId } from '../AddressList';
import AddressSuggestions from '../AddressSuggestions';
import DynamicFieldArea from '../DynamicFieldArea';
import './style.scss';

export type AddressFormProps = {
  headline?: ReactNode;
  context: 'shippingAddress' | 'billingAddress';
  availableCountries: Country[];
  isOverview?: boolean;
  addressSuggestions?: AddressSuggestion[];
  setAddressSuggestions: (addressSuggestions?: AddressSuggestion[]) => void;
};

const specialComponents: Record<Country, (props: any) => JSX.Element> = {
  BG: AddressFieldBg,
  NL: AddressFieldNl,
  US: AddressFieldUS
};

const AddressForm = (props: AddressFormProps) => {
  const { values, setFieldValue, setFieldTouched, submitForm, validateForm } = useFormikContext<any>();
  const { formatMessage, formatDisplayName } = useIntl();
  const {
    checkoutState: { orderState },
    isLoading
  } = useCheckoutState();
  const { toggleModal } = useModal();

  const { headline, context, availableCountries, isOverview, addressSuggestions } = props;
  const selectedCountry: Country = values[context].country;
  const fullCountryNamesEnabled = config.features.fullCountryNames.enabled;
  const isForwardToCollectionPointEnabled = config.deliveryOptions.forwardToCollectionPoint.enabled;

  const countries = availableCountries
    .map(value => ({
      label: fullCountryNamesEnabled ? formatDisplayName(value, { type: 'region' }) : value,
      value,
      icon: getAssetUrl(`flags/${value.toLowerCase()}.svg`)
    }))
    .sort((a, b) => a.label!.localeCompare(b.label!));

  const closeModal = () => {
    toggleModal();
    props.setAddressSuggestions(undefined);
  };

  useEffect(() => {
    if (addressSuggestions) {
      const onSelect = (address: AddressWithId) => {
        setFieldValue(context, { ...values[context], ...getInitialAddressValues(address) });
        submitForm();
        closeModal();
      };

      const modalConfig = {
        children: (
          <AddressSuggestions
            addresses={addressSuggestions}
            context={context}
            onClose={closeModal}
            onSelect={onSelect}
          />
        ),
        onEscapeKey: closeModal,
        onClickOutside: closeModal
      };
      toggleModal(modalConfig);
    }
  }, [addressSuggestions]);

  const showAddressBookModal = () => {
    const onSelect = (address: AddressWithId) => {
      toggleModal();
      const newValues = getInitialAddressValues(address);

      setFieldValue(context, { ...values[context], ...newValues });
      setTimeout(() => {
        Object.keys(newValues).forEach(key => setFieldTouched(`${context}.${key}`));
        validateForm();
      }, 0);
    };

    const addressBookModalConfig = {
      children: <AddressBook context={context} onClose={toggleModal} onSelect={onSelect} />,
      onEscapeKey: toggleModal,
      onClickOutside: toggleModal
    };
    toggleModal(addressBookModalConfig);
  };

  if (!selectedCountry) {
    return (
      <div>
        {headline && (
          <div className="address-form__headline">
            <SubHeadline>{headline}</SubHeadline>
          </div>
        )}
        <div className="form">
          <div
            className={classNames('form__row', {
              'form__row--column': fullCountryNamesEnabled
            })}
          >
            <Paragraph style={{ margin: 'auto 8px' }}>
              <FormattedMessage id="address.selectCountry" />
            </Paragraph>
            <div
              className={classNames(
                'form__field-container',
                'address-form__top-dropdown',
                'address-form__country-dropdown',
                { 'address-form--field-full-width': fullCountryNamesEnabled }
              )}
              data-test-id="address-form-country-dropdown"
            >
              <Field
                name={`${context}.country`}
                component={SelectField}
                label={formatMessage({ id: 'address.country' })}
                options={countries}
                data-test-id={`${context}-country`}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  const { title: titleConfig, specialComponent } = config.addressForms[selectedCountry];
  const AddressFormComponent =
    specialComponent && selectedCountry in specialComponents ? specialComponents[selectedCountry] : AddressField;

  const onCountryChange = () => {
    setFieldValue(`${context}.street`, '');
    setFieldTouched(`${context}.street`, false);
    setFieldValue(`${context}.houseNumber`, '');
    setFieldTouched(`${context}.houseNumber`, false);
    setFieldValue(`${context}.streetHouseNumber`, '');
    setFieldTouched(`${context}.streetHouseNumber`, false);
    setFieldValue(`${context}.additional`, '');
    setFieldTouched(`${context}.additional`, false);
    setFieldValue(`${context}.zipCode`, '');
    setFieldTouched(`${context}.zipCode`, false);
    setFieldValue(`${context}.city`, '');
    setFieldTouched(`${context}.city`, false);
    setFieldValue(`${context}.state`, '');
    setFieldTouched(`${context}.state`, false);
  };

  return (
    <FormNameProvider formName={context}>
      {headline && (
        <div className="address-form__headline" data-test-id="shipping-address-headline">
          <SubHeadline>{headline}</SubHeadline>
        </div>
      )}
      <div className="form">
        {config.features.addressBook.enabled && (
          <Button
            className="address-form__address-book-btn"
            type="button"
            disabled={!!isLoading}
            onClick={showAddressBookModal}
            data-test-id="address-book-btn"
          >
            <FormattedMessage id="address.addressBook" />
          </Button>
        )}
        <div
          className={classNames('form__row', 'form__row--three-wide', {
            'form__row--flow-reversed': fullCountryNamesEnabled && titleConfig?.enabled
          })}
        >
          <div className="form__field-container address-form__top-dropdown" data-test-id="address-form-gender-dropdown">
            <GenderField name={`${context}.gender`} testIdPrefix={context} />
          </div>
          {titleConfig?.enabled && (
            <div className={classNames('form__field-container', 'address-form__top-dropdown')}>
              <Field
                name={`${context}.title`}
                component={SelectField}
                label={formatMessage({ id: 'address.title.label' })}
                options={['profPhD', 'phD', 'prof'].map(value => ({
                  label: formatMessage({ id: `address.title.options.${value}` }),
                  value
                }))}
                isClearable
                data-test-id={`${context}-title`}
              />
            </div>
          )}
          <div
            className={classNames('form__field-container address-form__country-dropdown', {
              'address-form--field-full-width': fullCountryNamesEnabled
            })}
            data-test-id="address-form-country-dropdown"
          >
            <Field
              name={`${context}.country`}
              component={SelectField}
              label={formatMessage({ id: 'address.country' })}
              options={countries}
              onChange={onCountryChange}
              data-test-id={`${context}-country`}
              autoComplete="country"
              limitInputLength={!!fullCountryNamesEnabled}
            />
          </div>
        </div>
        <div className="form__row form__row--names">
          <NamesField testIdPrefix={context} context={context} />
        </div>

        <AddressFormComponent testIdPrefix={context} context={context} />

        {!isOverview && (
          <DynamicFieldArea
            area={context}
            context={context}
            conditionValues={{
              carrierGroupKey: context === 'shippingAddress' ? getCarriers(orderState) : undefined,
              shippingOptionKey: values.carrier?.shippingOptionKey,
              shippingCountry: context === 'shippingAddress' ? selectedCountry : undefined,
              billingCountry: context === 'billingAddress' ? selectedCountry : undefined
            }}
          />
        )}

        {isForwardToCollectionPointEnabled && context === 'shippingAddress' && (
          <div className="form__field-container">
            <Field
              name={`${context}.forwardToCollectionPoint`}
              component={CheckboxField}
              label={formatMessage({ id: 'address.forwardToCollectionPoint' })}
              data-test-id={`${context}-forward-to-collection-point`}
            />
          </div>
        )}
      </div>
    </FormNameProvider>
  );
};

export default AddressForm;
